import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Hand } from "lucide-react";
import { TIMINGS } from "@/hooks/useCallState";

// Press-and-hold button that respects:
//  - 250ms quick-tap protection
//  - Keyboard space-bar press-and-hold
//  - Mode flip: in 'handsfree' it acts as a tap-to-interrupt button
export default function PushToTalkButton({
  mode,
  callState,
  disabled,
  onTurnStart,
  onTurnEnd,
  onInterrupt,
}) {
  const downAtRef = useRef(null);
  const heldRef = useRef(false);
  const spaceDownRef = useRef(false);
  // Visual pressed state — the button lights up ONLY while actually held.
  const [pressed, setPressed] = useState(false);

  const isHandsfree = mode === "handsfree";
  const speakingNow = callState === "speaking";

  const handleDown = useCallback(
    (e) => {
      if (disabled) return;
      e?.preventDefault?.();
      if (isHandsfree) {
        // Visual press feedback in hands-free too — brief gold flash.
        setPressed(true);
        setTimeout(() => setPressed(false), 250);
        if (speakingNow) onInterrupt?.();
        return;
      }
      if (heldRef.current) return;
      downAtRef.current = Date.now();
      heldRef.current = true;
      setPressed(true);
      onTurnStart?.();
    },
    [disabled, isHandsfree, speakingNow, onTurnStart, onInterrupt],
  );

  const handleUp = useCallback(
    (e) => {
      if (disabled) return;
      e?.preventDefault?.();
      if (isHandsfree) return;
      if (!heldRef.current) return;
      heldRef.current = false;
      setPressed(false);
      const dur = Date.now() - (downAtRef.current || 0);
      if (dur < TIMINGS.MIN_PTT_HOLD_MS) {
        // quick-tap protection: do nothing, don't emit a turn
        return;
      }
      onTurnEnd?.();
    },
    [disabled, isHandsfree, onTurnEnd],
  );

  // Spacebar press-and-hold (only in PTT mode)
  useEffect(() => {
    if (isHandsfree || disabled) return undefined;
    const kd = (ev) => {
      if (ev.code === "Space" && !ev.repeat && !spaceDownRef.current) {
        // Only respond if focus is on the body or the button (avoid stealing from text fields)
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        if (["input", "textarea", "select"].includes(tag)) return;
        ev.preventDefault();
        spaceDownRef.current = true;
        handleDown();
      }
    };
    const ku = (ev) => {
      if (ev.code === "Space" && spaceDownRef.current) {
        ev.preventDefault();
        spaceDownRef.current = false;
        handleUp();
      }
    };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    return () => {
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
    };
  }, [isHandsfree, disabled, handleDown, handleUp]);

  // Color logic:
  //  - PTT: gold only while physically held
  //  - Hands-free: gold while the mic is hot (listening) — plus tap flash
  const listening = callState === "listening";
  const processing = callState === "processing" || callState === "interrupted";
  const isActive = !disabled && (pressed || (isHandsfree && listening));

  // Label states — UNIFIED across PTT and Hands-Free per user spec:
  //   • mic open (PTT held / handsfree listening) → "speak"
  //   • AI replying                               → "Jesus speaking, push to interrupt"
  //   • thinking                                  → "thinking…"
  //   • idle / ready                              → "push to talk"
  // The button visuals already distinguish the mode (Mic vs Hand icon); the
  // label stays consistent so users don't have to relearn it.
  const micOpen = pressed || (isHandsfree && listening);
  let labelMain;
  if (disabled) {
    labelMain = "push to talk";
  } else if (speakingNow) {
    labelMain = "Jesus speaking, push to interrupt";
  } else if (processing) {
    labelMain = "thinking…";
  } else if (micOpen) {
    labelMain = "speak";
  } else {
    labelMain = "push to talk";
  }

  const ariaLabel = isHandsfree ? "Tap to interrupt" : "Hold to speak";

  return (
    <div className="flex w-full flex-col items-center">
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid="ptt-button"
        data-mode={mode}
        data-state={callState}
        data-active={isActive ? "1" : "0"}
        onPointerDown={handleDown}
        onPointerUp={handleUp}
        onPointerLeave={handleUp}
        onPointerCancel={handleUp}
        className="relative inline-flex items-center justify-center rounded-full select-none"
        style={{
          width: "112px",
          height: "112px",
          background: isActive
            ? "linear-gradient(180deg, hsl(41 75% 64%) 0%, hsl(36 55% 48%) 100%)"
            : disabled
              ? "hsla(38,15%,82%,0.6)"
              : "hsl(36 10% 9%)",
          color: isActive ? "hsl(36 30% 18%)" : disabled ? "hsl(30 9% 50%)" : "hsl(43 45% 96%)",
          boxShadow: isActive
            ? "0 0 0 8px hsla(41,75%,75%,0.35), 0 24px 60px -18px hsla(36,55%,40%,0.65), inset 0 1px 0 hsla(0,0%,100%,0.7)"
            : disabled
              ? "none"
              : "0 18px 40px -16px hsla(36,10%,9%,0.55), inset 0 1px 0 hsla(0,0%,100%,0.1)",
          transition: "transform 150ms ease-out, box-shadow 200ms ease-out, background 200ms ease-out, color 200ms ease-out",
          transform: isActive ? "scale(1.04)" : "scale(1)",
          cursor: disabled ? "not-allowed" : "pointer",
          touchAction: "none",
          // iOS Safari: stop long-press text selection / callout stealing the hold
          WebkitUserSelect: "none",
          userSelect: "none",
          WebkitTouchCallout: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {/* Pulsing ring while active, or while God speaks in hands-free
            (draws the eye to the tap-to-interrupt affordance) */}
        {isActive || (isHandsfree && speakingNow && !disabled) ? (
          <span
            aria-hidden
            className="absolute rounded-full anim-ptt-ring"
            style={{
              inset: "-14px",
              border: "1.5px solid hsla(41,75%,55%,0.55)",
            }}
          />
        ) : null}
        {isHandsfree ? (
          <Hand className="h-9 w-9" strokeWidth={1.6} />
        ) : (
          <Mic className="h-10 w-10" strokeWidth={1.6} />
        )}
      </button>
      <div className="mt-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
        {labelMain}
      </div>
    </div>
  );
}
